import { useRef, useState } from "react";
import { useProfile } from "./hooks/useProfile";
import SectionTitle from "../../components/ui/SectionTitle";
import UserAvatar from "../../components/ui/icons/UserAvatar";
import Dropdown from "../../components/common/Dropdown";
import InfoRow from "../../components/ui/InfoRow";
import Field from "../../components/common/Field";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { accountSettingsValidationSchema } from "./validations/accountSettings.validation";

import TabPageLayout from "../../app/layouts/TabPageLayout";
import { createTicket, updateProfile } from "./api/auth.api";
import { Link } from "react-router-dom";
import useOrganization from "../organization/hooks/useOrganization";
import useSettings from "../settings/hooks/useSettings";

const AccountSettings: React.FC = () => {
  document.title = `Account Settings - ${process.env.REACT_APP_NAME}`;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user, departmentsLeading } = useProfile();
  const { currentOrganization } = useOrganization();
  const { birthYearOptions, genderOptions, raceOptions } = useSettings();

  const [isLoading, setIsLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [isImageRemoved, setIsImageRemoved] = useState(false);

  const [emailRequestSent, setEmailRequestSent] = useState(false);
  const [emailRequestLoading, setEmailRequestLoading] = useState(false);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setIsImageRemoved(false);
  };

  const demographic = (() => {
    try {
      return user?.demographic_data ? JSON.parse(user.demographic_data) : {};
    } catch {
      return {};
    }
  })();

  // ── Handle email change request via ticket API ───────────────────────
  const handleEmailChangeRequest = async () => {
    if (emailRequestSent) {
      toast.error("You have already submitted an email change request.");
      return;
    }

    setEmailRequestLoading(true);
    try {
      const departmentId = departmentsLeading?.[0]?.id ?? null;
      const organizationId = currentOrganization?.id;

      await createTicket({
        subject: "Email Change Request",
        body: `User ${user?.first_name} ${user?.last_name} (${user?.email}) has requested an email address change. Please review and process this request.`,
        status: 1,
        organization_id: organizationId,
        department_id: departmentId,
        user_id: user?.id ?? null,
      });

      setEmailRequestSent(true);
      toast.success(
        "Your email change request has been submitted successfully!",
      );
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ??
        "Failed to submit request. Please try again.",
      );
    } finally {
      setEmailRequestLoading(false);
    }
  };
  const confirmRemoveImage = async () => {
    try {
      setIsLoading(true);

      await updateProfile(formik.values, undefined, 0);

      setImageFile(null);
      setAvatarPreview(null);
      setIsImageRemoved(true);

      toast.success("Image removed successfully");
      setShowRemoveModal(false);
      window.location.reload();

    } catch (err) {
      toast.error("Failed to remove image");
    } finally {
      setIsLoading(false);
    }
  };

  const accountInfo = [
    { label: "Email Address", value: user?.email ?? "-", isLink: true },
  ];

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      nickname: user?.nick_name ?? "",
      year: demographic?.age ?? "",
      gender: demographic?.gender ?? "",
      race: demographic?.race ?? "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: accountSettingsValidationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {

        let is_image: number | undefined = undefined;

        // ✅ REMOVE IMAGE
        if (isImageRemoved) {
          is_image = 0;
        }

        // ✅ UPLOAD IMAGE
        if (imageFile) {
          is_image = 1;
        }

        const response = await updateProfile(
          values,
          imageFile ?? undefined,
          is_image
        );
        if (response?.success) {
          toast.success(response.message ?? "Profile updated!");

          formik.setFieldValue("currentPassword", "");
          formik.setFieldValue("newPassword", "");
          formik.setFieldValue("confirmPassword", "");

          formik.setFieldTouched("currentPassword", false);
          formik.setFieldTouched("newPassword", false);
          formik.setFieldTouched("confirmPassword", false);
          window.location.reload();
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message ?? "Something went wrong");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const fieldError = (name: keyof typeof formik.values) =>
    formik.touched[name] && formik.errors[name] ? (
      <p className="form-error">{formik.errors[name] as string}</p>
    ) : null;



  return (
    <TabPageLayout>
      <div className="bg-white rounded-xl shadow-[0_2px_12px_rgba(0,0,0,0.06)] max-w-[1304px] font-primary">
        <div className="px-[30px] py-8 lg:px-[35px]">
          <SectionTitle>Account Settings</SectionTitle>

          {/* Profile photo */}
          <div className="flex items-center gap-5 mb-8">
            <UserAvatar
              src={avatarPreview ?? user?.image ?? undefined}
              className="w-[72px] h-[72px] sm:w-[88px] sm:h-[88px] flex-shrink-0"
            />

            <div>
              <div className="text-dark-blue text-[14px] font-medium mb-3">
                Profile Photo
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn btn-normal btn-sm"
                  style={{ fontSize: 14, paddingLeft: 20, paddingRight: 20 }}
                >
                  {imageFile ? "Change Image" : "Upload Image"}
                </button>

                {(avatarPreview || user?.image) && (
                  <button
                    type="button"
                    onClick={() => setShowRemoveModal(true)}
                    className="btn btn-danger btn-sm"
                  >
                    Remove
                  </button>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </div>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="flex flex-col laptop:flex-row gap-8 lg:gap-12">
              {/* ── Editable fields ── */}
              <div className="flex-1 flex flex-col gap-5 min-w-0 xl:max-w-[703px]">
                {/* Row 1: Nickname + Birth Year */}
                <div className="grid grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-1 gap-5">
                  <Field label="Nickname">
                    <input
                      name="nickname"
                      type="text"
                      value={formik.values.nickname}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="login-input"
                    />
                    {fieldError("nickname")}
                  </Field>

                  <Field label="Select your birth year">
                    <Dropdown
                      id="year-select"
                      options={birthYearOptions}
                      value={formik.values.year}
                      onChange={(val) => formik.setFieldValue("year", val)}
                      placeholder="Select birth year"
                    />
                    {fieldError("year")}
                  </Field>
                </div>

                {/* Row 2: Gender + Race */}
                <div className="grid grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-1 gap-5">
                  <Field label="Select your gender">
                    <Dropdown
                      id="gender-select"
                      options={genderOptions}
                      value={formik.values.gender}
                      onChange={(val) => formik.setFieldValue("gender", val)}
                      placeholder="Select gender"
                    />
                    {fieldError("gender")}
                  </Field>

                  <Field label="Select your race">
                    <Dropdown
                      id="race-select"
                      options={raceOptions}
                      value={formik.values.race}
                      onChange={(val) => formik.setFieldValue("race", val)}
                      placeholder="Select race"
                    />
                    {fieldError("race")}
                  </Field>
                </div>

                {/* Row 3: Current Password + New Password */}
                <div className="grid grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-1 gap-5">
                  <Field label="Confirm Current Password">
                    <input
                      name="currentPassword"
                      type="password"
                      value={formik.values.currentPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="login-input"
                    />
                    {fieldError("currentPassword")}
                  </Field>

                  <Field label="New Password">
                    <input
                      name="newPassword"
                      type="password"
                      value={formik.values.newPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="login-input"
                    />
                    {fieldError("newPassword")}
                  </Field>
                </div>

                {/* Row 4: Confirm New Password */}
                <Field label="Confirm New Password">
                  <input
                    name="confirmPassword"
                    type="password"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="login-input"
                  />
                  {fieldError("confirmPassword")}
                </Field>

                <p
                  className="text-[#26295e]"
                  style={{ fontSize: 13, lineHeight: 1.6, fontWeight: 400 }}
                >
                  Your new password must be between 8 and 30 characters, contain
                  at least one uppercase letter, one lowercase letter, one
                  number digit and one special character.
                </p>

                {/* Actions */}
                <div className="flex items-center justify-end gap-6 pt-2">
                  <button
                    type="button"
                    onClick={() => formik.resetForm()}
                    className="text-[#9c9c9c] text-sm font-semibold bg-transparent border-none cursor-pointer transition-opacity hover:opacity-80 font-primary text-[15px]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-normal btn-sm md:btn-md"
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>

              {/* ── Read-only info ── */}
              <div className="xl:max-w-[346px] flex-shrink-0">
                <div className="lg:hidden h-px bg-[#f0f3fa] mb-6" />
                <div className="flex flex-col divide-y divide-[#f0f3fa]">
                  {accountInfo.map((row) => (
                    <InfoRow
                      key={row.label}
                      label={row.label}
                      value={row.value}
                      isLink={row.isLink}
                      onRequestChange={handleEmailChangeRequest}
                      requestSent={emailRequestSent}
                      requestLoading={emailRequestLoading}
                    />
                  ))}
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="border-t border-[#f0f3fa] px-6 py-4 sm:px-10 text-center">
          <span className="text-[#26295e] font-primary text-[13px]">
            Have questions?{" "}
            <Link
              to="/help"
              className="text-[#3b519c] underline hover:no-underline transition"
            >
              Contact Us
            </Link>
          </span>
        </div>
      </div>
      {showRemoveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl p-6 w-[90%] max-w-[400px] shadow-lg">
            <h3 className="text-lg font-semibold mb-3">
              Remove Profile Image?
            </h3>

            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to remove your profile image?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRemoveModal(false)}
                className="px-4 py-2 text-sm text-gray-500"
              >
                Cancel
              </button>

              <button
                onClick={confirmRemoveImage}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-md"
                disabled={isLoading}
              >
                {isLoading ? "Removing..." : "Yes, Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </TabPageLayout>

  );
};

export default AccountSettings;
